import {
	CategoryScale,
	Chart as ChartJS,
	Filler,
	Legend,
	LinearScale,
	LineElement,
	PointElement,
	Tooltip,
} from "chart.js";

import zoomPlugin from "chartjs-plugin-zoom";
import { Line } from "react-chartjs-2";
import { createGradient, crosshairPlugin } from "./utils.js";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Filler,
	Tooltip,
	Legend,
	zoomPlugin,
);

function TelemetryChart(props) {
	const { data: chartData, label = "Value", unit = "" } = props;

	const isKbMb = unit?.toLowerCase() === "kb/mb";

	const resolveDisplay = (value) => {
		if (!isKbMb) return { displayValue: value, displayUnit: unit };
		if (value < 1) return { displayValue: value * 1000, displayUnit: "kbps" };
		return { displayValue: value, displayUnit: "mbps" };
	};

	/**
	 * Formats a number with the minimum decimal places needed to show a
	 * meaningful value — avoids both unnecessary precision and loss of info.
	 * e.g. 0.00099 → "0.00099", 5.5 → "5.50", 0 → "0"
	 */
	const formatValue = (num) => {
		if (num === 0) return "0";
		if (Math.abs(num) >= 1) return num.toFixed(2);
		// Find how many decimal places are needed to show 2 significant digits
		const decimals = Math.max(2, Math.ceil(-Math.log10(Math.abs(num))) + 1);
		return num.toFixed(decimals);
	};

	const data = {
		labels: chartData.labels,
		datasets: [
			{
				label: label,
				data: chartData.values,
				fill: true,
				backgroundColor: (context) => {
					const chart = context.chart;
					const { ctx, chartArea } = chart;
					if (!chartArea) return "rgba(31, 96, 196, 0.2)";
					return createGradient(ctx, chartArea);
				},
				borderColor: "#4889FA",
				borderWidth: 0.5,
				tension: 0.35,
				pointRadius: 0,
				pointHoverRadius: 0,
				pointHitRadius: 20,
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		animation: true,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				enabled: true,
				mode: "nearest",
				intersect: false,
				backgroundColor: "rgba(22, 25, 37, 0.95)",
				titleColor: "#fff",
				bodyColor: "#fff",
				borderColor: "rgba(255, 255, 255, 0.1)",
				borderWidth: 1,
				padding: 12,
				displayColors: true,
				boxWidth: 8,
				boxHeight: 8,
				callbacks: {
					title: function (context) {
						const index = context[0].dataIndex;
						const timestamp = chartData.timestamps?.[index];

						if (timestamp) {
							const date = new Date(timestamp * 1000);
							return date.toLocaleString("en-US", {
								year: "numeric",
								month: "short",
								day: "numeric",
								hour: "2-digit",
								minute: "2-digit",
							});
						}
						return context[0].label;
					},
					label: function (context) {
						const value = context.parsed.y;
						const { displayValue, displayUnit } = resolveDisplay(value);
						return ` ${label}: ${formatValue(displayValue)}${displayUnit ? " " + displayUnit : ""}`;
					},
					labelColor: function () {
						return {
							borderColor: "#4889FA",
							backgroundColor: "#4889FA",
						};
					},
				},
				caretSize: 6,
				caretPadding: 10,
			},
			zoom: {
				pan: {
					enabled: true,
					mode: "x",
				},
				zoom: {
					wheel: {
						enabled: true,
					},
					pinch: {
						enabled: true,
					},
					drag: {
						enabled: true,
						backgroundColor: "rgba(31, 96, 196, 0.2)",
					},
					mode: "x",
				},
			},
		},
		scales: {
			x: {
				display: false,
			},
			y: {
				grid: {
					display: false,
					drawBorder: false,
				},
				min: label === "Obstruction" ? 0 : label === "Ping Drop Rate" ? 0 : undefined,
				max: label === "Obstruction" ? 1 : label === "Ping Drop Rate" ? 100 : undefined,
				ticks: {
					color: "rgba(255, 255, 255, .8)",
					font: {
						size: 11,
					},
					callback: function (value) {
						const { displayValue, displayUnit } = resolveDisplay(value);
						return formatValue(displayValue) + (displayUnit ? " " + displayUnit : "");
					},
					maxTicksLimit: 6,
				},
				border: {
					display: false,
				},
				beginAtZero: true,
			},
		},
		interaction: {
			intersect: false,
			mode: "nearest",
			axis: "x",
		},
	};

	return (
		<div
			style={{
				height: "320px",
				backgroundColor: "#111217",
				padding: "1em",
				borderRadius: "4px",
				marginBlockEnd: "1em",
			}}
		>
			<Line data={data} options={options} plugins={[crosshairPlugin]} />
		</div>
	);
}

export default TelemetryChart;
